import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ApiServiceShared } from '../services/ApiServiceShared';
import PasswordInput from './shared/PasswordInput';

interface Props {
  isRTL: boolean;
  t: (key: string) => string;
  onBack: () => void;
}

type Gender = 'Male' | 'Female';
type ParentMode = 'new' | 'existing';

interface Child {
  student_name: string;
  gender: Gender;
  level: string;
  halqa: string;
  notes: string;
}

interface ExistingChild {
  id: number;
  student_name: string;
  level?: string;
  halqa?: string;
  gender: Gender;
  already_reenrolled: boolean;
}

const HALQA_KEYS = ['inscriptionHalqaListen', 'inscriptionHalqaMushaf', 'inscriptionHalqaReview'] as const;
const HALQA_VALUES = ['حلقة الحفظ بالسمع', 'حلقة الحفظ بالمصحف', 'حلقة المراجعة'];
const LEVEL_KEYS = [
  'inscriptionLvlNursery', 'inscriptionLvl1', 'inscriptionLvl2',
  'inscriptionLvl3', 'inscriptionLvl4', 'inscriptionLvl5', 'inscriptionLvlPrep',
] as const;
const LEVEL_VALUES = ['الروض', 'الأول', 'الثاني', 'الثالث', 'الرابع', 'الخامس', 'الإعدادي'];

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 12px',
  borderRadius: '8px',
  border: '1.5px solid #D1D5DB',
  fontSize: '13px',
  color: '#1F2937',
  backgroundColor: '#FFFFFF',
  boxSizing: 'border-box',
  outline: 'none',
  fontFamily: 'Cairo, sans-serif',
  transition: 'border-color 0.2s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '5px',
};

const sectionStyle: React.CSSProperties = {
  backgroundColor: '#FFFFFF',
  borderRadius: '16px',
  padding: '20px',
  marginBottom: '14px',
  border: '1px solid #E5E7EB',
  boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
};

const radioRow: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  flexWrap: 'wrap',
};

/** RadioOpt – défini à l'extérieur pour éviter le remount */
const RadioOpt = ({ value, current, onChange, label }: { value: string; current: string; onChange: (v: string) => void; label: string }) => (
  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', flexDirection: 'row' }}>
    <input
      type="radio"
      value={value}
      checked={current === value}
      onChange={() => onChange(value)}
      style={{ accentColor: '#059669', width: '16px', height: '16px' }}
    />
    <span style={{ fontSize: '14px', fontWeight: '600', color: current === value ? '#059669' : '#374151' }}>{label}</span>
  </label>
);

/** SelectField – défini à l'extérieur pour éviter le remount */
const SelectField = ({ value, onChange, options, placeholder }: { value: string; onChange: (v: string) => void; options: { label: string; val: string }[]; placeholder: string }) => (
  <select
    value={value}
    onChange={e => onChange(e.target.value)}
    style={{ ...inputStyle, color: value ? '#1F2937' : '#9CA3AF' }}
  >
    <option value="">{placeholder}</option>
    {options.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
  </select>
);

interface ChildFormProps {
  child: Child;
  index: number;
  onUpdate: (i: number, f: keyof Child, v: string) => void;
  onRemove: (i: number) => void;
  showRemove: boolean;
  t: (key: string) => string;
  textAlign: 'left' | 'right';
}

/** ChildForm – défini à l'extérieur pour éviter le remount qui fait perdre le focus */
const ChildForm: React.FC<ChildFormProps> = ({ child, index, onUpdate, onRemove, showRemove, t, textAlign }) => (
  <div style={{ borderRadius: '8px', backgroundColor: '#F9FAFB', padding: '12px', marginBottom: '10px', border: '1px solid #E5E7EB' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
      <span style={{ fontSize: '13px', fontWeight: '700', color: '#374151' }}>{t('inscriptionChild')} {index + 1}</span>
      {showRemove && (
        <button onClick={() => onRemove(index)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}>
          {t('inscriptionRemoveChild')}
        </button>
      )}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
      <div style={{ gridColumn: '1 / -1' }}>
        <label style={{ ...labelStyle, textAlign }}>{t('inscriptionChildName')} <span style={{ color: '#DC2626' }}>*</span></label>
        <input style={inputStyle} value={child.student_name} onChange={e => onUpdate(index, 'student_name', e.target.value)} />
      </div>
      <div>
        <label style={{ ...labelStyle, textAlign }}>{t('inscriptionChildLevel')}</label>
        <SelectField value={child.level} onChange={v => onUpdate(index, 'level', v)}
          options={LEVEL_KEYS.map((k, idx) => ({ label: t(k), val: LEVEL_VALUES[idx] }))}
          placeholder={t('inscriptionChildLevel')} />
      </div>
      <div>
        <label style={{ ...labelStyle, textAlign }}>{t('inscriptionChildHalqa')}</label>
        <SelectField value={child.halqa} onChange={v => onUpdate(index, 'halqa', v)}
          options={HALQA_KEYS.map((k, idx) => ({ label: t(k), val: HALQA_VALUES[idx] }))}
          placeholder={t('inscriptionChildHalqa')} />
      </div>
    </div>
    <div>
      <label style={{ ...labelStyle, textAlign }}>{t('inscriptionGender')}</label>
      <div style={radioRow}>
        <RadioOpt value="Male" current={child.gender} onChange={v => onUpdate(index, 'gender', v)} label={t('inscriptionGenderMale')} />
        <RadioOpt value="Female" current={child.gender} onChange={v => onUpdate(index, 'gender', v)} label={t('inscriptionGenderFemale')} />
      </div>
    </div>
  </div>
);

const InscriptionPage: React.FC<Props> = ({ isRTL, t, onBack }) => {
  const dir = isRTL ? 'rtl' : 'ltr';
  const textAlign = isRTL ? 'right' : 'left';

  const [parentMode, setParentMode] = useState<ParentMode>('new');

  // Champs nouveau parent
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [gender, setGender] = useState<Gender>('Male');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');

  // Enfants (nouveau parent)
  const [children, setChildren] = useState<Child[]>([
    { student_name: '', gender: 'Male', level: '', halqa: '', notes: '' },
  ]);

  // Parent existant — step 1 : credentials
  const [existingUsername, setExistingUsername] = useState('');
  const [existingPassword, setExistingPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  // Parent existant — step 2 : après vérification
  const [verifiedParent, setVerifiedParent] = useState<{ parent_id: number; full_name: string; phone?: string | null; school_year_label?: string } | null>(null);
  const [existingChildren, setExistingChildren] = useState<ExistingChild[]>([]);
  const [reenrollIds, setRenrollIds] = useState<Set<number>>(new Set());
  const [newChildren, setNewChildren] = useState<Child[]>([]);
  // Téléphone de contact (obligatoire pour la demande) — prérempli depuis le compte parent s'il existe
  const [existingPhone, setExistingPhone] = useState('');

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedId, setSubmittedId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [inscriptionYearLabel, setInscriptionYearLabel] = useState<string | null>(null);

  // Charger l'année ouverte aux inscriptions
  React.useEffect(() => {
    ApiServiceShared.getInscriptionYear().then((res: any) => {
      if (res.success && res.data?.label) setInscriptionYearLabel(res.data.label);
    });
  }, []);

  const updateChild = (i: number, field: keyof Child, value: string) => {
    setChildren(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };

  const updateNewChild = (i: number, field: keyof Child, value: string) => {
    setNewChildren(prev => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };

  // Vérification credentials parent existant
  const handleVerifyParent = async () => {
    setVerifyError('');
    if (!existingUsername.trim() || !existingPassword.trim()) {
      setVerifyError(t('inscriptionErrCredentialsRequired'));
      return;
    }
    setVerifying(true);
    try {
      const res = await ApiServiceShared.verifyParent(existingUsername.trim(), existingPassword.trim());
      if (res.success && res.data) {
        setVerifiedParent({
          parent_id: res.data.parent_id,
          full_name: res.data.full_name,
          phone: res.data.phone ?? null,
          school_year_label: res.data.school_year_label,
        });
        // Préremplir le téléphone (vide si le compte n'en a pas → l'utilisateur doit le saisir)
        setExistingPhone(res.data.phone || '');
        const kids: ExistingChild[] = res.data.existing_children || [];
        setExistingChildren(kids);
        // Pré-sélectionner les enfants non encore réinscrits
        setRenrollIds(new Set(kids.filter(k => !k.already_reenrolled).map(k => k.id)));
        setNewChildren([]);
      } else {
        setVerifyError(t('inscriptionErrCredentials'));
      }
    } catch {
      setVerifyError(t('inscriptionErrGeneric'));
    } finally {
      setVerifying(false);
    }
  };

  const validate = (): string => {
    if (parentMode === 'new') {
      if (!fullName.trim()) return t('inscriptionErrName');
      if (!phone.trim()) return t('inscriptionErrPhone');
      if (children.some(c => !c.student_name.trim())) return t('inscriptionErrChildName');
    } else {
      if (!verifiedParent) return t('inscriptionErrCredentialsRequired');
      if (!existingPhone.trim()) return t('inscriptionErrPhone');
      const filledNew = newChildren.filter(c => c.student_name.trim());
      if (reenrollIds.size === 0 && filledNew.length === 0)
        return t('inscriptionErrAtLeastOne') || 'Sélectionnez au moins une réinscription ou ajoutez un nouvel enfant';
      if (newChildren.some(c => c.student_name && !c.student_name.trim())) return t('inscriptionErrChildName');
    }
    return '';
  };

  const handleSubmit = async () => {
    setError('');
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    try {
      let payload: Record<string, unknown>;
      if (parentMode === 'existing') {
        const filledNew = newChildren.filter(c => c.student_name.trim());
        payload = {
          request_type: 'parent',
          gender: 'Male',
          phone: existingPhone.trim(),
          children: filledNew.map(c => ({ ...c, student_name: c.student_name.trim(), level: c.level || undefined, halqa: c.halqa || undefined })),
          reenroll_student_ids: Array.from(reenrollIds),
          existing_parent_username: existingUsername.trim(),
          existing_parent_password: existingPassword.trim(),
        };
      } else {
        payload = {
          request_type: 'parent',
          full_name: fullName.trim(),
          phone: phone.trim(),
          email: email.trim() || undefined,
          gender,
          address: address.trim() || undefined,
          notes: notes.trim() || undefined,
          children: children.map(c => ({ ...c, student_name: c.student_name.trim(), level: c.level || undefined, halqa: c.halqa || undefined })),
        };
      }

      const res = await ApiServiceShared.submitInscription(payload);
      if (res.success) {
        setSubmittedId(res.data?.id ?? null);
        setSubmitted(true);
      } else {
        if (res.statusCode === 429) setError(t('inscriptionErrLimit'));
        else if (parentMode === 'existing') setError(t('inscriptionErrCredentials'));
        else setError(t('inscriptionErrGeneric'));
      }
    } finally {
      setLoading(false);
    }
  };

  const btnPrimary: React.CSSProperties = {
    backgroundColor: '#059669',
    color: '#FFF',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 28px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: 'Cairo, sans-serif',
    transition: 'opacity 0.2s',
    opacity: loading ? 0.7 : 1,
  };

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', direction: dir }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          style={{ backgroundColor: '#FFF', borderRadius: '16px', padding: '40px 32px', maxWidth: '420px', width: '100%', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}
        >
          <div style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '2.5rem' }}>✅</div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#1F2937', marginBottom: '12px' }}>{t('inscriptionSuccess')}</h2>
          <p style={{ color: '#6B7280', marginBottom: '24px', lineHeight: '1.6' }}>{t('inscriptionSuccessMsg')}</p>
          {submittedId && (
            <div style={{ backgroundColor: '#D1FAE5', borderRadius: '10px', padding: '12px 20px', marginBottom: '24px' }}>
              <div style={{ fontSize: '12px', color: '#059669', marginBottom: '4px' }}>{t('inscriptionRequestNumber')}</div>
              <div style={{ fontSize: '1.5rem', fontWeight: '800', color: '#059669' }}>#{submittedId}</div>
            </div>
          )}
          <button onClick={onBack} style={btnPrimary}>{t('inscriptionBack')}</button>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F3F4F6', direction: dir, fontFamily: 'Cairo, sans-serif' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto', padding: '16px 16px 40px' }}>

        {/* Header */}
        <div style={{
          backgroundColor: '#c5f1dd', border: '1px solid #4ed38e', borderRadius: '12px',
          padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', marginBottom: '14px',
        }}>
          <button onClick={onBack} style={{
            position: 'absolute', [isRTL ? 'right' : 'left']: '10px',
            background: 'rgb(215, 240, 224)', border: '1px solid #14a894 207, 153)',
            cursor: 'pointer', fontSize: '0.95rem', color: '#025f02',
            padding: '5px 11px', borderRadius: '8px', lineHeight: 1,
            display: 'flex', alignItems: 'center', fontFamily: 'Cairo, sans-serif', fontWeight: '600',
            boxShadow: '0 1px 3px #548d91',
          }}>
            {isRTL ? '→' : '←'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
            <span style={{ fontSize: '1rem' }}>📋</span>
            <h1 style={{ fontSize: '1rem', fontWeight: '700', color: '#059669', margin: 0 }}>{t('inscriptionTitle')}</h1>
            {inscriptionYearLabel && (
              <span style={{ fontSize: '11px', fontWeight: '700', color: '#047857', backgroundColor: '#A7F3D0', borderRadius: '6px', padding: '2px 8px' }}>
                {inscriptionYearLabel}
              </span>
            )}
          </div>
        </div>

        {/* Toggle mode */}
        <div style={{ ...sectionStyle, padding: '6px' }}>
          <div style={{ display: 'flex', borderRadius: '12px', overflow: 'hidden', gap: '4px', padding: '4px', backgroundColor: '#F3F4F6' }}>
            {(['new', 'existing'] as ParentMode[]).map(mode => (
              <button key={mode} onClick={() => { setParentMode(mode); setVerifiedParent(null); setVerifyError(''); setExistingPhone(''); }}
                style={{
                  flex: 1, padding: '10px 8px', border: 'none', borderRadius: '10px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '700', fontFamily: 'Cairo, sans-serif', transition: 'all 0.2s',
                  backgroundColor: parentMode === mode ? '#059669' : 'transparent',
                  color: parentMode === mode ? '#FFFFFF' : '#6B7280',
                  boxShadow: parentMode === mode ? '0 2px 8px rgba(5,150,105,0.3)' : 'none',
                }}>
                {mode === 'new' ? t('inscriptionParentModeNew') : t('inscriptionParentModeExisting')}
              </button>
            ))}
          </div>
        </div>

        {/* ── Nouveau parent ── */}
        {parentMode === 'new' && (<>
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>👤</div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#059669', textAlign }}>{t('inscriptionTypeParent')}</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
              <div>
                <label style={{ ...labelStyle, textAlign }}>{t('inscriptionName')} <span style={{ color: '#DC2626' }}>*</span></label>
                <input style={inputStyle} value={fullName} onChange={e => setFullName(e.target.value)} />
              </div>
              <div>
                <label style={{ ...labelStyle, textAlign }}>{t('inscriptionPhone')} <span style={{ color: '#DC2626' }}>*</span></label>
                <input style={inputStyle} value={phone} onChange={e => setPhone(e.target.value)} dir="ltr" />
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ ...labelStyle, textAlign }}>{t('inscriptionEmail')}</label>
              <input style={inputStyle} value={email} onChange={e => setEmail(e.target.value)} dir="ltr" />
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ ...labelStyle, textAlign }}>{t('inscriptionGender')}</label>
              <div style={radioRow}>
                <RadioOpt value="Male" current={gender} onChange={v => setGender(v as Gender)} label={t('inscriptionGenderMale')} />
                <RadioOpt value="Female" current={gender} onChange={v => setGender(v as Gender)} label={t('inscriptionGenderFemale')} />
              </div>
            </div>
            <div style={{ marginBottom: '10px' }}>
              <label style={{ ...labelStyle, textAlign }}>{t('inscriptionAddress')}</label>
              <input style={inputStyle} value={address} onChange={e => setAddress(e.target.value)} />
            </div>
            <div>
              <label style={{ ...labelStyle, textAlign }}>{t('inscriptionNotes')}</label>
              <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>

          {/* Enfants nouveau parent */}
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>🧒</div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#059669', textAlign }}>{t('inscriptionChildrenTitle')}</div>
            </div>
            {children.map((child, i) => (
              <ChildForm key={i} child={child} index={i} onUpdate={updateChild}
                onRemove={idx => setChildren(prev => prev.filter((_, j) => j !== idx))}
                showRemove={children.length > 1} t={t} textAlign={textAlign} />
            ))}
            <button
              onClick={() => setChildren(prev => [...prev, { student_name: '', gender: 'Male', level: '', halqa: '', notes: '' }])}
              style={{ background: 'none', border: '1px dashed #059669', borderRadius: '8px', color: '#059669', padding: '10px', width: '100%', cursor: 'pointer', fontSize: '14px', fontWeight: '600', fontFamily: 'Cairo, sans-serif' }}>
              {t('inscriptionAddChild')}
            </button>
          </div>
        </>)}

        {/* ── Parent existant ── */}
        {parentMode === 'existing' && (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', paddingBottom: '12px', borderBottom: '1px solid #F3F4F6' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '8px', backgroundColor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem' }}>🔑</div>
              <div style={{ fontSize: '13px', fontWeight: '800', color: '#059669', textAlign }}>{t('inscriptionParentModeExisting')}</div>
            </div>

            {/* Step 1 : credentials */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
              <div>
                <label style={{ ...labelStyle, textAlign }}>{t('inscriptionExistingUsername')} <span style={{ color: '#DC2626' }}>*</span></label>
                <input style={inputStyle} value={existingUsername} onChange={e => { setExistingUsername(e.target.value); setVerifiedParent(null); }} dir="ltr" disabled={!!verifiedParent} />
              </div>
              <div>
                <label style={{ ...labelStyle, textAlign }}>{t('inscriptionExistingPassword')} <span style={{ color: '#DC2626' }}>*</span></label>
                <PasswordInput value={existingPassword} onChange={e => { setExistingPassword(e.target.value); setVerifiedParent(null); }}
                  autoComplete="current-password" dir="ltr" style={{ ...inputStyle, opacity: verifiedParent ? 0.6 : 1 }} />
              </div>
            </div>

            {verifyError && (
              <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', color: '#DC2626', fontSize: '13px' }}>
                {verifyError}
              </div>
            )}

            {!verifiedParent && (
              <button onClick={handleVerifyParent} disabled={verifying}
                style={{ backgroundColor: '#059669', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '14px', fontWeight: '700', cursor: verifying ? 'wait' : 'pointer', fontFamily: 'Cairo, sans-serif', opacity: verifying ? 0.7 : 1 }}>
                {verifying ? `⏳ ${t('inscriptionVerifying') || '…'}` : (t('inscriptionVerify') || 'Vérifier')}
              </button>
            )}

            {/* Step 2 : après vérification */}
            {verifiedParent && (<>
              {/* Confirmation identité */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#D1FAE5', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
                <span style={{ fontSize: '1.2rem' }}>✅</span>
                <span style={{ fontSize: '13px', fontWeight: '700', color: '#065f46' }}>{verifiedParent.full_name}</span>
                {(verifiedParent.school_year_label || inscriptionYearLabel) && (
                  <span style={{ fontSize: '11px', color: '#047857', backgroundColor: '#A7F3D0', borderRadius: '4px', padding: '2px 7px' }}>
                    {verifiedParent.school_year_label || inscriptionYearLabel}
                  </span>
                )}
              </div>

              {/* Téléphone de contact — obligatoire (inscription_requests.phone est NOT NULL) */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ ...labelStyle, textAlign }}>{t('inscriptionPhone')} <span style={{ color: '#DC2626' }}>*</span></label>
                <input style={inputStyle} value={existingPhone} onChange={e => setExistingPhone(e.target.value)} dir="ltr" />
                {!verifiedParent.phone && (
                  <div style={{ fontSize: '11px', color: '#B45309', marginTop: '4px', textAlign }}>
                    {t('inscriptionPhoneMissingHint') || 'Aucun numéro enregistré sur votre compte : merci de le saisir pour finaliser la demande.'}
                  </div>
                )}
              </div>

              {/* ── Groupe Réinscription des enfants ── */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: '800', color: '#374151', marginBottom: '10px', textAlign,
                  display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>🔄</span>
                  <span>{t('inscriptionRenewSection') || 'Réinscription des enfants'}</span>
                </div>

                {existingChildren.length === 0 ? (
                  <div style={{ backgroundColor: '#F9FAFB', border: '1px dashed #D1D5DB', borderRadius: '8px',
                    padding: '12px', textAlign: 'center', color: '#9CA3AF', fontSize: '13px' }}>
                    {t('inscriptionNoChildrenFound') || 'Aucun enfant trouvé pour ce compte'}
                  </div>
                ) : (
                  existingChildren.map(ch => {
                    const isSelected = reenrollIds.has(ch.id);
                    const toggle = () => {
                      if (ch.already_reenrolled) return;
                      setRenrollIds(prev => {
                        const next = new Set(prev);
                        if (next.has(ch.id)) next.delete(ch.id); else next.add(ch.id);
                        return next;
                      });
                    };
                    return (
                      <div key={ch.id} onClick={!ch.already_reenrolled ? toggle : undefined}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          backgroundColor: ch.already_reenrolled ? '#F0FDF4' : isSelected ? '#ECFDF5' : '#F9FAFB',
                          border: `1.5px solid ${ch.already_reenrolled ? '#86EFAC' : isSelected ? '#34D399' : '#E5E7EB'}`,
                          borderRadius: '10px', padding: '10px 12px', marginBottom: '8px',
                          cursor: ch.already_reenrolled ? 'default' : 'pointer',
                          transition: 'all 0.15s',
                        }}>
                        {ch.already_reenrolled ? (
                          <span style={{ fontSize: '1rem' }}>✅</span>
                        ) : (
                          <input type="checkbox" checked={isSelected} onChange={toggle}
                            onClick={e => e.stopPropagation()}
                            style={{ accentColor: '#059669', width: '16px', height: '16px', flexShrink: 0 }} />
                        )}
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937', flex: 1 }}>{ch.student_name}</span>
                        {ch.level && (
                          <span style={{ fontSize: '11px', color: '#6B7280', backgroundColor: '#E5E7EB', borderRadius: '4px', padding: '2px 6px' }}>
                            {ch.level}
                          </span>
                        )}
                        {ch.already_reenrolled ? (
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#059669', backgroundColor: '#D1FAE5', borderRadius: '6px', padding: '2px 8px', whiteSpace: 'nowrap' }}>
                            {t('inscriptionAlreadyRenrolled') || 'Déjà réinscrit'}
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', fontWeight: '600',
                            color: isSelected ? '#047857' : '#6B7280', whiteSpace: 'nowrap' }}>
                            {isSelected ? (t('inscriptionSelected') || '✓ Sélectionné') : (t('inscriptionSelectRenroll') || 'Réinscrire')}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* ── Nouveaux enfants (optionnel) ── */}
              <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '800', color: '#374151', marginBottom: newChildren.length > 0 ? '10px' : '8px', textAlign,
                  display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>➕</span>
                  <span>{t('inscriptionNewChildren') || 'Nouveaux enfants à inscrire'}</span>
                </div>
                {newChildren.map((child, i) => (
                  <ChildForm key={i} child={child} index={i} onUpdate={updateNewChild}
                    onRemove={idx => setNewChildren(prev => prev.filter((_, j) => j !== idx))}
                    showRemove={true} t={t} textAlign={textAlign} />
                ))}
                <button
                  onClick={() => setNewChildren(prev => [...prev, { student_name: '', gender: 'Male', level: '', halqa: '', notes: '' }])}
                  style={{ background: 'none', border: '1px dashed #059669', borderRadius: '8px', color: '#059669',
                    padding: '10px', width: '100%', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                    fontFamily: 'Cairo, sans-serif' }}>
                  {t('inscriptionAddChild')}
                </button>
              </div>
            </>)}
          </div>
        )}

        {/* Erreur soumission */}
        {error && (
          <div style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', color: '#DC2626', fontSize: '14px', textAlign }}>
            {error}
          </div>
        )}

        {/* Bouton soumettre — visible si nouveau parent ou parent existant vérifié */}
        {(parentMode === 'new' || verifiedParent) && (() => {
          const noAction =
            parentMode === 'existing' &&
            !!verifiedParent &&
            reenrollIds.size === 0 &&
            !newChildren.some(c => c.student_name.trim());
          const btnDisabled = loading || noAction;
          return (
            <motion.button
              whileHover={{ scale: btnDisabled ? 1 : 1.02 }}
              whileTap={{ scale: btnDisabled ? 1 : 0.98 }}
              onClick={handleSubmit}
              disabled={btnDisabled}
              style={{
                ...btnPrimary,
                width: '100%', padding: '16px', fontSize: '16px',
                background: btnDisabled ? '#9CA3AF' : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                boxShadow: btnDisabled ? 'none' : '0 4px 16px rgba(5,150,105,0.35)',
                borderRadius: '14px',
                cursor: btnDisabled ? 'not-allowed' : 'pointer',
                opacity: noAction ? 0.7 : 1,
              }}>
              {loading ? `⏳ ${t('inscriptionSubmitting')}` : `✅ ${t('inscriptionSubmit')}`}
            </motion.button>
          );
        })()}
      </div>
    </div>
  );
};

export default InscriptionPage;